import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  Trip, TripListResponse, TripListParams, TripInput, TripPreviewMathResponse,
} from '@/lib/types';

export async function fetchTrips(params: TripListParams): Promise<TripListResponse> {
  const { data } = await apiClient.get<TripListResponse>('/api/v1/trips', { params });
  return data;
}

export async function fetchTrip(id: string): Promise<Trip> {
  const { data } = await apiClient.get<{ trip: Trip }>(`/api/v1/trips/${id}`);
  return data.trip;
}

export async function createTrip(input: TripInput): Promise<Trip> {
  const { data } = await apiClient.post<{ trip: Trip }>('/api/v1/trips', input);
  return data.trip;
}

export async function updateTrip(id: string, input: Partial<TripInput>): Promise<Trip> {
  const { data } = await apiClient.patch<{ trip: Trip }>(`/api/v1/trips/${id}`, input);
  return data.trip;
}

export async function previewTripMath(
  siteContractId: string,
  deliveredQty: number,
  investorId?: string,
): Promise<TripPreviewMathResponse> {
  const { data } = await apiClient.get<TripPreviewMathResponse>('/api/v1/trips/preview-math', {
    params: { siteContractId, deliveredQty, investorId },
  });
  return data;
}

export async function confirmTripById(id: string): Promise<Trip> {
  const { data } = await apiClient.post<{ trip: Trip }>(`/api/v1/trips/${id}/confirm`);
  return data.trip;
}

export async function cancelTripById(id: string): Promise<Trip> {
  const { data } = await apiClient.post<{ trip: Trip }>(`/api/v1/trips/${id}/cancel`);
  return data.trip;
}

export async function disputeTripById(id: string): Promise<Trip> {
  const { data } = await apiClient.post<{ trip: Trip }>(`/api/v1/trips/${id}/dispute`);
  return data.trip;
}

// Hooks
export function useTrips(params: TripListParams) {
  return useQuery({
    queryKey: ['trips', params],
    queryFn: () => fetchTrips(params),
    placeholderData: (prev) => prev,
  });
}

export function useTrip(id: string | null | undefined) {
  return useQuery({
    queryKey: ['trip', id],
    queryFn: () => fetchTrip(id!),
    enabled: !!id,
  });
}

export function useCreateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createTrip,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trips'] }),
  });
}

export function useUpdateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<TripInput> }) =>
      updateTrip(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['trips'] });
      qc.setQueryData(['trip', updated.id], updated);
    },
  });
}

export function useConfirmTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: confirmTripById,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['trips'] });
      qc.setQueryData(['trip', updated.id], updated);
    },
  });
}

export function useCancelTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cancelTripById,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['trips'] });
      qc.setQueryData(['trip', updated.id], updated);
    },
  });
}

export function useDisputeTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: disputeTripById,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['trips'] });
      qc.setQueryData(['trip', updated.id], updated);
    },
  });
}
